import { toast } from "react-toastify";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { Box, IconButton } from "@mui/material";
import { Input, Modal, Popconfirm, Tooltip } from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axios/axios-instance";
import ActionButton from "../../components/common/buttons/ActionButton";
import CancelButton from "../../components/common/buttons/CancelButton";
import Card from "../../components/common/Card";
import SelectField from "../../components/common/fields/SelectField";
import StatusBadge from "../../components/common/StatusBadge";
import MuiTable from "../../components/common/MuiTable";
import AddModal from "../../components/Coupon/AddModal";
import { Column } from "../../utils/constants";
import { pathAPI } from "../../utils/pathApi";
import CircularProgress from "@mui/material/CircularProgress";
import { formatNumber } from "../../utils/utils";
import { useMe } from "../../auth/MeContext";
import { PERM } from "../../auth/permissions";

const STATUS_TEXT_COLOR: Record<string, string> = {
  PENDING: "#ed6c02",
  APPROVED: "#2e7d32",
  REJECTED: "#d32f2f",
};

const STATUS_FILTER_WIDTH = 200;
const STATUS_FILTER_VALUES = ["PENDING", "APPROVED", "REJECTED"];

const Coupon = () => {
  const [openModalAdd, setOpenModalAdd] = useState(false);
  const [openModalEdit, setOpenModalEdit] = useState(false);
  const [openModalDetail, setOpenModalDetail] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<any>(null);
  const [couponDetail, setCouponDetail] = useState<any>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const { t } = useTranslation();
  const { can } = useMe();
  const statusFilterOptions = STATUS_FILTER_VALUES.map((value) => ({
    value,
    label: t(`coupon.list.status.${value}`),
  }));
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [rejecting, setRejecting] = useState<{ id: number; reason: string } | null>(null);
  const [dataTable, setDataTable] = useState([]);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [editingCouponId, setEditingCouponId] = useState<number | null>(null);
  const header: Column[] = [
    {
      id: "coupon_name",
      label: `${t("common.table.name")}`,
      minWidth: 20,
      align: "center",
    },
    {
      id: "coupon_code",
      label: `${t("common.table.code")}`,
      minWidth: 20,
      align: "center",
    },
    {
      id: "coupon_amount",
      label: `${t("common.table.amount")}`,
      minWidth: 100,
      align: "center",
      format: (value) => {
        return (
          <div className="font-bold">
            {formatNumber(value)}
          </div>
        );
      }
    },
    {
      id: "coupon_type",
      label: `${t("common.table.type")}`,
      minWidth: 50,
      align: "center",
    },
    {
      id: "start_time",
      label: `${t("common.table.start_time")}`,
      minWidth: 100,
      align: "center",
      format: (value) => {
        return <div>{moment(value).format("YYYY/MM/DD HH:mm:ss")}</div>;
      },
    },
    {
      id: "end_time",
      label: `${t("common.table.end_time")}`,
      minWidth: 100,
      align: "center",
      format: (value) => {
        return <div>{moment(value).format("YYYY/MM/DD HH:mm:ss")}</div>;
      },
    },
    {
      id: "target",
      label: t("coupon.list.target"),
      minWidth: 100,
      align: "center",
      format: (coupon: any) => {
        if (coupon.target_type === "COUNTRY") {
          return <div>{(coupon.countries ?? []).map((c: any) => c.name).join(", ") || "-"}</div>;
        }
        return <div>{t("coupon.list.salons_count", { n: (coupon.salons ?? []).length })}</div>;
      },
    },
    {
      id: "approval_status",
      label: t("coupon.list.approval"),
      minWidth: 80,
      align: "center",
      format: (value: string, row?: any) => (
        <Tooltip title={row?.reject_reason || ""}>
          <span style={{ color: STATUS_TEXT_COLOR[value], fontWeight: 500 }}>{value ? t(`coupon.list.status.${value}`, { defaultValue: value }) : "-"}</span>
        </Tooltip>
      ),
    },
    {
      id: "active",
      label: `${t("common.table.active")}`,
      minWidth: 20,
      align: "center",
      format: (value: any) => {
        return <StatusBadge label={`${value}`} tone={value ? "success" : "error"} />;
      },
    },
    {
      id: "action",
      label: `${t("common.table.actions")}`,
      minWidth: 100,
      align: "center",
      format: (value) => {
        return (
          <Box
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
            gap={2}
          >
            {can(PERM.COUPON_APPROVE) && value.approval_status === "PENDING" && (
              <>
                <Popconfirm
                  title={t("coupon.list.approve_confirm")}
                  okText={t("btn.confirm")}
                  cancelText={t("btn.cancel")}
                  onConfirm={() => handleApprove(value.id)}
                >
                  <ActionButton>{t("coupon.list.approve")}</ActionButton>
                </Popconfirm>
                <CancelButton onClick={() => setRejecting({ id: value.id, reason: "" })}>
                  {t("coupon.list.reject")}
                </CancelButton>
              </>
            )}
            {can(PERM.COUPON_UPDATE) && (
            <Box
              size={"small"}
              component={IconButton}
              disabled={loadingDelete || editingCouponId === value.id}
              onClick={() => handleEdit(value)}
            >
              {editingCouponId === value.id ? (
                <CircularProgress size={24} />
              ) : (
                <EditIcon color="success" />
              )}
            </Box>
            )}
            {can(PERM.COUPON_DELETE) && (
            <Popconfirm
              title={t("help_support.delete_text")}
              okText={t("btn.confirm")}
              cancelText={t("btn.cancel")}
              onConfirm={() => handleDelete(value.id)}
              okButtonProps={{
                style: { background: "#ba2156", border: "none" },
                loading: loadingDelete,
              }}
            >
              <Box size={"small"} component={IconButton}>
                <DeleteIcon color={"error"} />
              </Box>
            </Popconfirm>
            )}
          </Box>
        );
      },
    },
  ];
  const handleApprove = (id: number) => {
    axiosInstance
      .put(`${pathAPI.coupon}/${id}/approve`)
      .then(() => {
        toast.success(t("coupon.list.approved_ok"));
        loadingDataTable();
      })
      .catch(() => toast.error(t("coupon.list.approved_err")));
  };

  const handleReject = () => {
    if (!rejecting || !rejecting.reason.trim()) {
      toast.warning(t("coupon.list.reason_required"));
      return;
    }
    axiosInstance
      .put(`${pathAPI.coupon}/${rejecting.id}/reject`, { reason: rejecting.reason.trim() })
      .then(() => {
        toast.success(t("coupon.list.rejected_ok"));
        setRejecting(null);
        loadingDataTable();
      })
      .catch(() => toast.error(t("coupon.list.rejected_err")));
  };

  const handleDelete = (id: number) => {
    setLoadingDelete(true);
    axiosInstance.delete(`${pathAPI.coupon}/${id}`)
      .then(() => {
        toast.success(t("coupon.list.deleted_ok"));
        loadingDataTable();
      })
      .catch((error: any) => {
        console.log("Error deleting coupon:", error);
        toast.error(t("coupon.list.deleted_err"));
      })
      .finally(() => {
        setLoadingDelete(false);
      });
  };

  const handleEdit = async (coupon: any) => {
    setEditingCouponId(coupon.id);
    await axiosInstance.get(`${pathAPI.coupon}/${coupon.id}`).then((response: any) => {
      setSelectedCoupon(response.data.data);
      setOpenModalEdit(true);
    }).catch((error: any) => {
      console.log("Error fetching coupon details:", error);
      toast.error(t("coupon.list.fetch_err"));
    }).finally(() => {
      setEditingCouponId(null);
    });
  };

  const handleCloseEditModal = () => {
    setOpenModalEdit(false);
    setSelectedCoupon(null);
  };

  const loadingDataTable = async() => {
    setLoading(true);
    await axiosInstance.get(pathAPI.coupon, { params: statusFilter ? { approvalStatus: statusFilter } : {} })
    .then((response: any) => {
      const finalyData = response.data.data.map((item: any) => {
        return {
          ...item,
          action: item,
          target: item
        }
      });
      setDataTable(finalyData);
    })
    .catch((error: any) => {
      console.log("==== get Coupon data table error: ", error);
    })
    .finally(() => {
      setLoading(false);
    })
  }

  useEffect(() => {
    loadingDataTable()
  }, [statusFilter])
  return (
    <Card>
      <Box display="flex" justifyContent="space-between" alignItems="center" my={2}>
        <Box width={STATUS_FILTER_WIDTH}>
          <SelectField
            value={statusFilter}
            emptyLabel={t("coupon.list.all_statuses")}
            searchable={false}
            matchAnchorWidth
            placeholder={t("coupon.list.all_statuses")}
            options={statusFilterOptions}
            onChange={(value) => setStatusFilter(value === null ? "" : String(value))}
          />
        </Box>
        {can(PERM.COUPON_CREATE) && (
          <ActionButton appSize="large" onClick={() => setOpenModalAdd(true)}>
            {t("coupon.list.add")}
          </ActionButton>
        )}
      </Box>
      <MuiTable
        header={header}
        data={dataTable}
        isLoading={isLoading}
      ></MuiTable>
      <AddModal open={openModalAdd} setOpen={setOpenModalAdd} reload={loadingDataTable}/>
      <AddModal 
        open={openModalEdit} 
        setOpen={handleCloseEditModal} 
        reload={loadingDataTable}
        isEdit={true}
        couponData={selectedCoupon}
      />
      <Modal
        open={!!rejecting}
        title={t("coupon.list.reject_title")}
        okText={t("coupon.list.reject")}
        cancelText={t("btn.cancel")}
        okButtonProps={{ danger: true }}
        onOk={handleReject}
        onCancel={() => setRejecting(null)}
      >
        <Input.TextArea
          rows={4}
          maxLength={500}
          placeholder={t("coupon.list.reject_placeholder")}
          value={rejecting?.reason ?? ""}
          onChange={(e) => setRejecting((prev) => (prev ? { ...prev, reason: e.target.value } : prev))}
        />
      </Modal>
    </Card>
  );
};
export default Coupon;
